import React, { Fragment } from 'react';
import moment from 'moment';
import {
    ChipField,
    Filter,
    EmailField,
    TextField,
    ShowButton,
    ReferenceField,
    ReferenceInput,
    SelectInput,
    DateInput,
    Link,
    BulkDeleteButton,
    BulkExportButton,
    FunctionField,
    SelectArrayInput,
    ReferenceArrayInput,
    AutocompleteArrayInput,
} from 'react-admin';
import { Chip } from '@material-ui/core';
import CustomSearchInput from '../components/CustomSearchInput';
import BoxitChipField from '../components/BoxitChipField';
import MoneyField from '../components/MoneyField';
import CountryField from '../components/CountryField';
import { UserType, UserStatus, Source } from '../constants';
import { ListProps, User } from '../types';
import UserReferenceField from '../components/UserReferenceField';
import AndOrInput from '../components/AndOrInput';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import { CreateButton, ExportButton, FilterButton } from 'react-admin';
import { BulkSendWatiTemplate } from './components';

const UserFilter = props => {
    return (
        <Filter variant="outlined" {...props}>
            <CustomSearchInput alwaysOn />
            {/*<AndOrInput label="Tags" alwaysOn />*/}
            <ReferenceInput
                source="partnerId"
                reference="partners"
                resource="partners"
                label="Partner"
            >
                <SelectInput />
            </ReferenceInput>

            <ReferenceInput
                source="countryId"
                reference="country"
                resource="country"
                label="Country"
            >
                <SelectInput />
            </ReferenceInput>

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="status"
                choices={Object.keys(UserStatus).map(c => ({ id: c, name: c }))}
            />
            <ReferenceArrayInput
                source="tagsand"
                reference="tags"
                label="Tags (And)"
            >
                <AutocompleteArrayInput />
            </ReferenceArrayInput>
            <ReferenceArrayInput
                source="tagsor"
                reference="tags"
                label="Tags (Or)"
            >
                <AutocompleteArrayInput />
            </ReferenceArrayInput>
            <SelectInput
                source="type"
                choices={Object.keys(UserType)
                    .filter(type => type !== 'staff')
                    .map(c => ({ id: c, name: c }))}
            />
            <DateInput
                source="createdAt[$gte]"
                label="Created From"
                parse={v => v && moment(v).startOf('day').format()}
            />
            <DateInput
                source="createdAt[$lte]"
                label="Created To"
                parse={v => v && moment(v).endOf('day').format()}
            />
            <SelectInput
                source="source"
                choices={Object.keys(Source).map(c => ({
                    id: c,
                    name: Source[c],
                }))}
            />
            <ReferenceInput
                source="assigneeId"
                reference="users"
                resource="users"
                label="Assignee"
                filter={{ userRoles: ['sales-staff'] }}
            >
                <SelectInput />
            </ReferenceInput>
        </Filter>
    );
};

// Custom field components with links
const PartnerField = ({ record }) => {
    if (!record || !record.partner) return null;
    return (
        <Link to={`/partners/${record.partner.id}/show`}>
            {record.partner.name}
        </Link>
    );
};

const CountryNameField = ({ record }) => {
    if (!record || !record.country) return null;
    return <span>{record.country.name}</span>;
};

const AssigneeField = ({ record }) => {
    if (!record || !record.assignee) return null;
    return (
        <Link to={`/users/${record.assignee.id}/show`}>
            {record.assignee.name}
        </Link>
    );
};

// BulkActionButtons component for the UserList
const BulkActionButtons = props => {
    // Check admin permissions from localStorage
    let userPermissions: string[] = [];
    try {
        const storedPermissions = localStorage.getItem('permissions');
        if (storedPermissions) {
            userPermissions = JSON.parse(storedPermissions);
        }
    } catch (error) {
        console.error('Error parsing permissions from localStorage:', error);
    }

    const isAdmin = userPermissions.includes('admin');

    return (
        <Fragment>
            {isAdmin && <BulkSendWatiTemplate {...props} />}
            <BulkExportButton {...props} />
            <BulkDeleteButton undoable={false} {...props} />
        </Fragment>
    );
};

const UserList: React.FC<ListProps<User>> = ({
    permissions = [],
    ...props
}) => {
    // Column configuration with default visibility and order
    const defaultColumns: ColumnConfig[] = [
        // Default visible columns
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'partner', label: 'Partner', visible: true, order: 2 },
        { id: 'type', label: 'Type', visible: true, order: 3 },
        { id: 'name', label: 'Name', visible: true, order: 4 },
        { id: 'email', label: 'Email', visible: true, order: 5 },
        { id: 'phone', label: 'Phone', visible: true, order: 6 },
        { id: 'assignee', label: 'Assignee', visible: true, order: 7 },
        { id: 'country', label: 'Country', visible: true, order: 8 },
        {
            id: 'credits',
            label: 'Credits',
            visible: permissions.indexOf('partner') === -1,
            order: 9,
        },
        { id: 'tags', label: 'Tags', visible: true, order: 10 },
        { id: 'status', label: 'Status', visible: true, order: 11 },
        { id: 'actions', label: 'Actions', visible: true, order: 12 },

        // Additional fields from API response (initially hidden)
        { id: 'isVerified', label: 'Is Verified', visible: false, order: 13 },
        { id: 'roles', label: 'Roles', visible: false, order: 14 },
        { id: 'currency', label: 'Currency', visible: false, order: 15 },
        { id: 'trn', label: 'TRN', visible: false, order: 16 },
        { id: 'persona', label: 'Persona', visible: false, order: 17 },
        {
            id: 'emailSignature',
            label: 'Email Signature',
            visible: false,
            order: 18,
        },
        {
            id: 'verifyShortToken',
            label: 'Verify Short Token',
            visible: false,
            order: 19,
        },
        { id: 'phone2', label: 'Phone 2', visible: false, order: 20 },
        { id: 'id1Type', label: 'ID 1 Type', visible: false, order: 21 },
        { id: 'id1Number', label: 'ID 1 Number', visible: false, order: 22 },
        { id: 'id1Expiry', label: 'ID 1 Expiry', visible: false, order: 23 },
        { id: 'id2Type', label: 'ID 2 Type', visible: false, order: 24 },
        { id: 'id2Number', label: 'ID 2 Number', visible: false, order: 25 },
        { id: 'id2Expiry', label: 'ID 2 Expiry', visible: false, order: 26 },
        { id: 'deposits', label: 'Deposits', visible: false, order: 27 },
        { id: 'storageRate', label: 'Storage Rate', visible: false, order: 28 },
        { id: 'addressId', label: 'Address ID', visible: false, order: 29 },
        {
            id: 'enableAutoCardCharging',
            label: 'Auto Card Charging',
            visible: false,
            order: 30,
        },
        {
            id: 'tap_customer_id',
            label: 'TAP Customer ID',
            visible: false,
            order: 31,
        },
        {
            id: 'tapPaymentAgreementId',
            label: 'TAP Payment Agreement',
            visible: false,
            order: 32,
        },
        { id: 'englishName', label: 'English Name', visible: false, order: 33 },
        { id: 'arabicName', label: 'Arabic Name', visible: false, order: 34 },
        {
            id: 'firstNameEnglish',
            label: 'First Name (English)',
            visible: false,
            order: 35,
        },
        {
            id: 'firstNameArabic',
            label: 'First Name (Arabic)',
            visible: false,
            order: 36,
        },
        {
            id: 'lastNameEnglish',
            label: 'Last Name (English)',
            visible: false,
            order: 37,
        },
        {
            id: 'lastNameArabic',
            label: 'Last Name (Arabic)',
            visible: false,
            order: 38,
        },
        {
            id: 'nicknameEnglish',
            label: 'Nickname (English)',
            visible: false,
            order: 39,
        },
        {
            id: 'nicknameArabic',
            label: 'Nickname (Arabic)',
            visible: false,
            order: 40,
        },
        { id: 'gender', label: 'Gender', visible: false, order: 41 },
        {
            id: 'currentStatus',
            label: 'Current Status',
            visible: false,
            order: 42,
        },
        { id: 'language', label: 'Language', visible: false, order: 43 },
        { id: 'createdAt', label: 'Created At', visible: false, order: 44 },
        { id: 'updatedAt', label: 'Updated At', visible: false, order: 45 },
    ];

    // Render a column based on its ID
    const renderColumn = columnId => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'type':
                return (
                    <FunctionField
                        key={columnId}
                        label="Type"
                        render={record => <Chip label={record.type} />}
                    />
                );
            case 'name':
                return <TextField key={columnId} source="name" />;
            case 'email':
                return <EmailField key={columnId} source="email" />;
            case 'phone':
                return <TextField key={columnId} source="phone" />;
            case 'assignee':
                return (
                    <FunctionField
                        key={columnId}
                        label="Assignee"
                        render={record => <AssigneeField record={record} />}
                    />
                );
            case 'country':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country"
                        render={record => <CountryNameField record={record} />}
                    />
                );
            case 'credits':
                return permissions.indexOf('partner') === -1 ? (
                    <MoneyField key={columnId} source="credits" showCurrency />
                ) : null;
            case 'tags':
                return (
                    <FunctionField
                        key={columnId}
                        label="Tags"
                        render={record =>
                            record.tags
                                ? record.tags.length > 3
                                    ? record.tags
                                          .map(item => item.name)
                                          .slice(0, 3)
                                          .join(', ') + '...'
                                    : record.tags
                                          .map(item => item.name)
                                          .slice(0, 3)
                                          .join(', ')
                                : ''
                        }
                    />
                );
            case 'status':
                return (
                    <FunctionField
                        key={columnId}
                        label="Status"
                        render={record => (
                            <BoxitChipField
                                record={record}
                                source="status"
                                list="user"
                            />
                        )}
                    />
                );
            case 'actions':
                return props.hasShow ? (
                    <ShowButton
                        key={columnId}
                        style={{ padding: 0 }}
                        basePath="/users"
                    />
                ) : null;
            case 'isVerified':
                return (
                    <FunctionField
                        key={columnId}
                        label="Is Verified"
                        render={record => (record.isVerified ? 'Yes' : 'No')}
                    />
                );
            case 'roles':
                return (
                    <FunctionField
                        key={columnId}
                        label="Roles"
                        render={record =>
                            record.roles
                                ? record.roles.length > 3
                                    ? record.roles.slice(0, 3).join(', ') +
                                      '...'
                                    : record.roles.join(', ')
                                : ''
                        }
                    />
                );
            case 'currency':
                return <TextField key={columnId} source="currency" />;
            case 'trn':
                return <TextField key={columnId} source="trn" />;
            case 'persona':
                return <TextField key={columnId} source="persona" />;
            case 'emailSignature':
                return <TextField key={columnId} source="emailSignature" />;
            case 'verifyShortToken':
                return <TextField key={columnId} source="verifyShortToken" />;
            case 'phone2':
                return <TextField key={columnId} source="phone2" />;
            case 'id1Type':
                return <TextField key={columnId} source="id1Type" />;
            case 'id1Number':
                return <TextField key={columnId} source="id1Number" />;
            case 'id1Expiry':
                return <TextField key={columnId} source="id1Expiry" />;
            case 'id2Type':
                return <TextField key={columnId} source="id2Type" />;
            case 'id2Number':
                return <TextField key={columnId} source="id2Number" />;
            case 'id2Expiry':
                return <TextField key={columnId} source="id2Expiry" />;
            case 'deposits':
                return <TextField key={columnId} source="deposits" />;
            case 'storageRate':
                return <TextField key={columnId} source="storageRate" />;
            case 'addressId':
                return <TextField key={columnId} source="addressId" />;
            case 'enableAutoCardCharging':
                return (
                    <FunctionField
                        key={columnId}
                        label="Auto Card Charging"
                        render={record =>
                            record.enableAutoCardCharging ? 'Yes' : 'No'
                        }
                    />
                );
            case 'tap_customer_id':
                return <TextField key={columnId} source="tap_customer_id" />;
            case 'tapPaymentAgreementId':
                return (
                    <TextField key={columnId} source="tapPaymentAgreementId" />
                );
            case 'englishName':
                return <TextField key={columnId} source="englishName" />;
            case 'arabicName':
                return <TextField key={columnId} source="arabicName" />;
            case 'firstNameEnglish':
                return <TextField key={columnId} source="firstNameEnglish" />;
            case 'firstNameArabic':
                return <TextField key={columnId} source="firstNameArabic" />;
            case 'lastNameEnglish':
                return <TextField key={columnId} source="lastNameEnglish" />;
            case 'lastNameArabic':
                return <TextField key={columnId} source="lastNameArabic" />;
            case 'nicknameEnglish':
                return <TextField key={columnId} source="nicknameEnglish" />;
            case 'nicknameArabic':
                return <TextField key={columnId} source="nicknameArabic" />;
            case 'gender':
                return <TextField key={columnId} source="gender" />;
            case 'currentStatus':
                return <TextField key={columnId} source="currentStatus" />;
            case 'language':
                return <TextField key={columnId} source="language" />;
            case 'createdAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Created At"
                        render={record =>
                            record.createdAt
                                ? new Date(record.createdAt).toLocaleString()
                                : ''
                        }
                    />
                );
            case 'updatedAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Updated At"
                        render={record =>
                            record.updatedAt
                                ? new Date(record.updatedAt).toLocaleString()
                                : ''
                        }
                    />
                );
            default:
                return null;
        }
    };

    // Ensure we always filter out staff users and mark this as a non-staff users list
    const nonStaffFilter = {
        'type[$ne]': 'staff',
        // Add a flag to identify this as a non-staff users list in the data mapper
        basePath: '/users',
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        permissions,
        defaultColumns,
        storageKey: 'userListColumns',
        renderColumn,
        bulkActionButtons: <BulkActionButtons />,
        filtersComponent: <UserFilter />,
        sort: { field: 'id', order: 'DESC' },
        perPage: 10,
        filterDefaultValues: { ...nonStaffFilter },
        metaParams: {},
        filter: nonStaffFilter,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'users',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

UserList.defaultProps = {
    resource: 'users',
    location: { search: '' },
    hasShow: true,
};

// react-beautiful-dnd is already installed and being used for column reordering

export default UserList;
